import React from 'react';
import { Box, Dialog } from '@mui/material';
import DashboardBg from 'assets/DashboardBg.png';
import CreateFreeAccount from 'pages/FreeTrialActivation/components/CreateFreeAccount';
import VerifyEmail from 'pages/FreeTrialActivation/components/VerifyEmail';
import CreatePassword from 'pages/FreeTrialActivation/components/CreatePassword';
import OrganizationInfo, { OrganizationInfoInitialValues } from 'pages/FreeTrialActivation/components/OrganizationInfo';
import PaymentInfo from 'pages/FreeTrialActivation/components/PaymentInfo';
import InviteTeam from 'pages/FreeTrialActivation/components/InviteTeam';
import SetupComplete from 'pages/FreeTrialActivation/components/SetupComplete';
import { FreeTrialOnboardingProvider, useFreeTrialOnboarding } from 'pages/FreeTrialActivation/components/FreeTrialOnboardingContext';
import { useHttp } from 'hooks/use-fetch';


function FreeTrialOnboardingInner() {
  const { setToastProps } = useHttp();
  const {
    step,
    setStep,
    emailMasked,
    setEmailMasked,
    providerId,
    setProviderId,
    token,
    setToken,
    skipPassword,
    setSkipPassword,
    pendingOrgStep,
    setPendingOrgStep,
    orgInitialValues,
    totalSteps,
    handleSilentLogin,
    onboardingStatus,
  } = useFreeTrialOnboarding();

  React.useEffect(() => {
    if (pendingOrgStep && skipPassword) {
      setStep(3);
      setPendingOrgStep(false);
    }
  }, [pendingOrgStep, skipPassword, setStep, setPendingOrgStep]);

  const handleCreateAccountNext = () => {
    if (onboardingStatus === 'PASSWORD_SET' || onboardingStatus === 'PRACTICE_ASSOCIATED' || onboardingStatus === 'ONBOARDING_COMPLETED') {
      setStep(2); // Go to VerifyEmail
    }
    else if (onboardingStatus === 'ONBOARDING_STARTED' || onboardingStatus === '') {
      setStep(1); // Go to OrganizationInfo
    }
    else {
      setStep(1); // Default: Go to OrganizationInfo
    }
  };

  const screens = [
    <CreateFreeAccount
      key='create'
      onNext={handleCreateAccountNext}
      setEmailMasked={setEmailMasked}
      setProviderId={setProviderId}
      setToastProps={setToastProps}
      step={1}
      totalSteps={totalSteps}
    />,
    <OrganizationInfo
      key='organizationinfo'
      onNext={() => setStep(2)}
      step={2}
      totalSteps={totalSteps}
      providerId={providerId}
      setToastProps={setToastProps}
      initialValues={orgInitialValues || undefined}
    />,
    <VerifyEmail
      key='verify'
      emailMasked={emailMasked}
      onNext={(tokenFromApi?: string, onboardingStatusEmail?: string, orgData?:
        OrganizationInfoInitialValues) => {
        if (tokenFromApi) setToken(tokenFromApi);
        if (onboardingStatusEmail === 'ONBOARDING_COMPLETED') {
          setToken(tokenFromApi);
          setTimeout(() => handleSilentLogin(tokenFromApi), 0);
        }
        else if (onboardingStatusEmail === 'PASSWORD_SET') {
          setSkipPassword(true);
          setStep(3); // Go to PaymentInfo (index is now 3 when skipPassword is true)
        }
        else {
          setStep(3);
        }
      }}
      onSilentLogin={(accessToken: string) => {
        setToken(accessToken);
        setTimeout(() => handleSilentLogin(accessToken), 0);
      }}
      step={3}
      totalSteps={totalSteps}
      providerId={providerId}
      setToken={setToken}
      setToastProps={setToastProps}
    />,
    !skipPassword && (
      <CreatePassword
        key='password'
        onNext={() => setStep(4)}
        step={4}
        totalSteps={totalSteps}
        token={token}
        setToastProps={setToastProps}
      />
    ),
    <PaymentInfo
      key='payment'
      onNext={() => setStep(step + 1)}
      step={skipPassword ? 4 : 5}
      totalSteps={totalSteps}
      setToastProps={setToastProps}
      token={token}
    />,
    <InviteTeam
      key='invite'
      onNext={() => setStep(step + 1)}
      step={skipPassword ? 5 : 6}
      totalSteps={totalSteps}
      token={token}
      setToastProps={setToastProps}
    />,
    <SetupComplete
      key='complete'
      onSilentLogin={() => handleSilentLogin(token)}
    />,
  ].filter(Boolean);

  const currentScreen = screens[step];
  const currentKey = React.isValidElement(currentScreen) ? currentScreen.key : undefined;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        minWidth: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 0,
        backgroundImage: `url(${DashboardBg})`,
        backgroundSize: 'cover',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Dialog
        open
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            background: '#FFF',
            boxShadow:
                '0 11px 15px -7px rgba(0,0,0,0.20), 0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12)',
            width: {
              xs: '100vw',
              sm: currentKey === 'payment' ? '1200px' : '800px',
            },
            minHeight: { xs: '100vh', sm: 'auto' },
            flexDirection: 'column',
            p: 0,
            m: 0,
            maxWidth: '100vw',
            maxHeight: '100vh',
            overflow: 'auto',
          },
        }}
      >
        {screens[step]}
      </Dialog>
    </Box>
  );
}

export default function FreeTrialOnboarding() {
  return (
    <FreeTrialOnboardingProvider>
      <FreeTrialOnboardingInner />
    </FreeTrialOnboardingProvider>
  );
}
